import React from 'react';
import {
  useTranslate,
  NumberInput,
  TextInput,
  SelectInput,
  FormDataConsumer,
  required,
} from 'react-admin';
import { AmountInput, ReferenceInput } from '~/components/ra';
import { GlTypeLabel } from '~/components';
import { Account } from '~/types';
import lodashGet from 'lodash/get';
import { useForm } from 'react-final-form';

import { l } from '~/resources/pricings/PricingShow';
import { ByFeature } from '..';
import { PricingFeeType } from '~/api/pricings';
import TierBased from './TierBased';
import CounterBased from './CounterBased';
import { number } from 'react-admin';
import { minValue } from 'react-admin';

const accountOptionText = (record?: Account) =>
  record ? <GlTypeLabel glType={record.glType} text={record.alias} /> : '';

interface Props {
  getSource: (key: string) => string;
  schema: ByFeature;
  currency: string;
  format?: string;
  tax?: boolean;
}

interface BaseFormProps extends Props {
  formData: any;
}

const BaseForm = (props: BaseFormProps) => {
  const { getSource, schema, formData, currency, format, tax } = props;
  const scopedFormData = lodashGet(formData, getSource('')) || {};

  const translate = useTranslate();
  const t = (key: string): string => translate(l(key), 1);

  const form = useForm();

  // When adding a new fee type, keep this in sync with:
  //   1. The switch() cases rendered below (add the new case's UI)
  //   2. cleanup() in PricingDataEdit.tsx (add submit-time field removal for the new type)
  const handleTypeChange = (e: any) => {
    const newType = e.target.value as string | undefined;

    const clear = (fields: string[]) =>
      fields.forEach((field) => form.change(getSource(field), undefined));

    if (!newType) {
      clear([
        'amount',
        'minAmount',
        'maxAmount',
        'percent',
        'tiers',
        'rules',
        'defaultRule',
        'dynamicTypeCounterReference',
      ]);
      return;
    }

    const typeLower = newType.toLowerCase();
    if (typeLower.includes('fixedpluspercent')) {
      clear(['tiers', 'rules', 'defaultRule', 'dynamicTypeCounterReference']);
    } else if (typeLower.includes('fixed')) {
      clear([
        'minAmount',
        'maxAmount',
        'percent',
        'tiers',
        'rules',
        'defaultRule',
        'dynamicTypeCounterReference',
      ]);
    } else if (typeLower.includes('percent')) {
      clear(['amount', 'tiers', 'rules', 'defaultRule', 'dynamicTypeCounterReference']);
    } else if (typeLower.includes('tiered')) {
      clear([
        'amount',
        'minAmount',
        'maxAmount',
        'percent',
        'rules',
        'defaultRule',
        'dynamicTypeCounterReference',
      ]);
    } else if (typeLower.includes('counter')) {
      clear(['amount', 'minAmount', 'maxAmount', 'percent', 'tiers']);
    }
  };

  const currencyChange = (e: any) => {
    const name = e.target.name;
    const minAmountName = getSource('minAmount.currency');
    const maxAmountName = getSource('maxAmount.currency');
    const currency = lodashGet(form.getState().values, name);
    if (currency && name?.endsWith('.currency')) {
      if (name !== minAmountName) {
        form.change(minAmountName, currency);
      }
      if (name !== maxAmountName) {
        form.change(maxAmountName, currency);
      }
    }
  };
  return (
    <>
      <SelectInput
        source={getSource('type')}
        choices={schema['v:enum']}
        label={tax ? t('taxType') : t('feeType')}
        allowEmpty
        emptyText='—'
        onChange={handleTypeChange}
      />
      {scopedFormData.type && (
        <>
          {format === 'v2' ? (
            <ReferenceInput
              source={getSource('pnlAccountId')}
              reference='tenant/accounts'
              label={t('pnlAccount')}
              validate={required()}
              filter={{ regulatoryType: 'EQUITY' }}
            >
              <SelectInput optionText={accountOptionText} />
            </ReferenceInput>
          ) : (
            <ReferenceInput
              source={getSource('pnlAccountId')}
              reference='tenant/accounts'
              label={t('pnlAccount')}
              validate={required()}
              filter={{ currency, regulatoryType: 'EQUITY' }}
            >
              <SelectInput optionText={accountOptionText} />
            </ReferenceInput>
          )}
          <TextInput source={getSource('name')} label={t('statementTitle')} validate={required()} />
        </>
      )}
      {(function () {
        switch (scopedFormData.type as PricingFeeType | undefined) {
          case 'fixedDebitFee':
          case 'fixedCreditFee':
            return (
              <AmountInput
                source={getSource('amount')}
                permanentSelectedCurrency={currency}
                required
                fullWidth
                positiveOnly
              />
            );
          case 'percentDebitFee':
          case 'percentCreditFee':
            return (
              <>
                <NumberInput
                  source={getSource('percent')}
                  label={t('percent')}
                  validate={[number(), minValue(0), required()]}
                />
                <AmountInput
                  source={getSource('minAmount')}
                  amountLabel={t('minAmount')}
                  permanentSelectedCurrency={currency}
                  onChange={currencyChange}
                  fullWidth
                  positiveOnly
                />
                <AmountInput
                  source={getSource('maxAmount')}
                  amountLabel={t('maxAmount')}
                  permanentSelectedCurrency={currency}
                  onChange={currencyChange}
                  fullWidth
                  positiveOnly
                />
              </>
            );
          case 'tieredDebitFee':
          case 'tieredCreditFee':
            return (
              <TierBased
                formData={formData}
                getSource={getSource}
                schema={schema}
                permanentSelectedCurrency={currency}
              />
            );
          case 'counterBasedDebitFee':
          case 'counterBasedCreditFee':
            return (
              <CounterBased
                formData={formData}
                getSource={getSource}
                schema={schema}
                permanentSelectedCurrency={currency}
              />
            );
          case 'fixedPlusPercentDebitFee':
          case 'fixedPlusPercentCreditFee':
            return (
              <>
                <AmountInput
                  source={getSource('amount')}
                  permanentSelectedCurrency={currency}
                  required
                  fullWidth
                  positiveOnly
                />
                <NumberInput
                  source={getSource('percent')}
                  label={t('percent')}
                  validate={[number(), minValue(0), required()]}
                />
                <AmountInput
                  source={getSource('minAmount')}
                  amountLabel={t('minAmountOfPercentPart')}
                  permanentSelectedCurrency={currency}
                  onChange={currencyChange}
                  fullWidth
                  positiveOnly
                />
                <AmountInput
                  source={getSource('maxAmount')}
                  amountLabel={t('maxAmountOfPercentPart')}
                  permanentSelectedCurrency={currency}
                  onChange={currencyChange}
                  fullWidth
                  positiveOnly
                />
              </>
            );
          default:
            return null;
        }
      })()}
    </>
  );
};

const FeeForm = (props: Props) => {
  const { getSource, schema, currency, format, tax } = props;

  return (
    <FormDataConsumer>
      {({ formData }: any) => {
        return (
          <>
            <BaseForm
              getSource={getSource}
              currency={currency}
              schema={schema}
              formData={formData}
              format={format}
              tax={tax}
            />
          </>
        );
      }}
    </FormDataConsumer>
  );
};

export default FeeForm;
